import React from 'react'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import CheckIcon from '@mui/icons-material/Check'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { alpha, useTheme } from '@mui/material/styles'
import { EventPayloadId, Notification } from '@/pages/NotificationCenter/types'
import { useNotificationActions } from '@/pages/NotificationCenter/hooks/useNotificationActions'
import {
  formatNotificationDate,
  formatAbsoluteDateTime,
  getNotificationTypeConfig,
  AVATAR_NOTIFICATIONS,
} from '@/pages/NotificationCenter/notificationHelpers'
import { generatePath, useNavigate } from 'react-router-dom'
import { RoutesData } from '@/router/RoutesData'
import {
  ItemContainer,
  NotificationContent,
  NotificationText,
  TopRow,
  IndicatorsContainer,
  UnreadIndicator,
  TimeStampRow,
  IconContainer,
} from './NotificationItem.styles'

interface NotificationItemProps {
  notification: Notification
  onClick?: () => void
}

const NotificationItem = ({ notification, onClick }: NotificationItemProps) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { markAsRead } = useNotificationActions({
    onSuccess: () => {
      // Success feedback handled silently
    },
    onError: () => {
      // Handle error silently or show user-friendly feedback
    },
  })

  const isUnread = notification.status === 'unread'
  const typeConfig = getNotificationTypeConfig(notification.type)

  // Fallback to NotificationsIcon if icon is not defined for this notification type
  const IconComponent = typeConfig.icon || NotificationsIcon
  const notificationColor = typeConfig.iconColor || '#007A95'
  const notificationIconBgColor = typeConfig?.bgColor || ''

  const caseId = notification.metadata?.case_id

  // Handler for check icon - mark as read only (no navigation)
  const handleMarkAsReadOnly = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent ItemContainer onClick
    if (isUnread) {
      markAsRead(notification.id)
    }
  }

  // Handler for notification item - action + mark as read
  const handleNotificationAction = () => {
    if (isUnread) {
      markAsRead(notification.id)
    }

    // Handle navigation if notification has action_url in metadata
    if (caseId) {
      navigate(generatePath(RoutesData.RCase.path, { id: caseId }))
    }

    onClick?.()
  }

  // Keyboard handler for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleNotificationAction()
    }
  }

  // Keyboard handler for mark as read button
  const handleMarkAsReadKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      if (isUnread) {
        markAsRead(notification.id)
      }
    }
  }

  // Check for avatar in metadata
  const avatarUrl =
    (notification.metadata?.buyer_attorney_avatar_url as string) ||
    (notification.metadata?.seller_attorney_avatar_url as string) ||
    ''

  const isAvatarNotificationType = AVATAR_NOTIFICATIONS.includes(
    notification.type as EventPayloadId
  )

  // Render left indicator (avatar or icon)
  const renderLeftIndicator = () => {
    if (!!avatarUrl || isAvatarNotificationType) {
      return <Avatar src={avatarUrl} sx={{ width: '36px', height: '36px' }} />
    }

    return (
      <IconContainer bgcolor={notificationIconBgColor}>
        <IconComponent sx={{ color: notificationColor, fontSize: 20 }} />
      </IconContainer>
    )
  }

  return (
    <ItemContainer
      tabIndex={0}
      aria-label={`Notification: ${notification.title}. ${
        isUnread ? 'Unread' : 'Read'
      }. ${notification.content}`}
      data-testid="notification-item"
      {...(caseId
        ? {
            onClick: handleNotificationAction,
            onKeyDown: handleKeyDown,
            role: 'button',
            cursor: 'pointer',
          }
        : {})}
    >
      <NotificationContent>
        {renderLeftIndicator()}

        <NotificationText>
          <TopRow>
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: 1,
                color: alpha(theme.palette.common.black, 0.87),
              }}
            >
              {notification.title}
            </Typography>
          </TopRow>

          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              color: alpha(theme.palette.common.black, 0.6),
            }}
          >
            {notification.content}
          </Typography>

          <TimeStampRow>
            <Typography
              variant="caption"
              sx={{ color: alpha(theme.palette.common.black, 0.38) }}
            >
              {formatNotificationDate(notification.created_at)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                whiteSpace: 'nowrap',
                textAlign: 'right',
                color: alpha(theme.palette.common.black, 0.38),
              }}
            >
              {formatAbsoluteDateTime(notification.created_at)}
            </Typography>
          </TimeStampRow>
        </NotificationText>

        {isUnread && (
          <Tooltip title="Mark as read" placement="left" arrow>
            <IndicatorsContainer
              onClick={handleMarkAsReadOnly}
              onKeyDown={handleMarkAsReadKeyDown}
              tabIndex={0}
              role="button"
              aria-label="Mark notification as read"
            >
              <CheckIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <UnreadIndicator />
            </IndicatorsContainer>
          </Tooltip>
        )}
      </NotificationContent>
    </ItemContainer>
  )
}

// Memoize component to prevent unnecessary re-renders
// Only re-render when notification data or onClick handler changes
const MemoizedNotificationItem = React.memo(
  NotificationItem,
  (prevProps, nextProps) =>
    prevProps.notification.id === nextProps.notification.id &&
    prevProps.notification.status === nextProps.notification.status &&
    prevProps.onClick === nextProps.onClick
)

MemoizedNotificationItem.displayName = 'NotificationItem'

export default MemoizedNotificationItem
